import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { type FormEvent, useState } from 'react';
import AgeGroupListForm, { type AgeGroupDraft } from './AgeGroupListForm';
import SettingsCrudDialog from './SettingsCrudDialog';
import SettingsListDeleteButton from './SettingsListDeleteButton';
import SettingsListEditButton from './SettingsListEditButton';
import SettingsListSection, { SettingsEmptyTableRow } from './SettingsListSection';
import { createAgeGroup, updateAgeGroup } from './ageGroupApi';
import type { AgeGroupRow } from './singleTableListSupport';
import {
  ageGroupIntervalLabel,
  formatSettingsRowDate,
  settingsListApiError,
} from './singleTableListSupport';
import { useAgeGroupList } from './useAgeGroupList';

const headSx = { fontWeight: 600, bgcolor: 'action.hover' } as const;

export default function AgeGroupList() {
  const { rows, loading, error, load, deleteById, deletingId } = useAgeGroupList<AgeGroupRow>();
  const [draft, setDraft] = useState<AgeGroupDraft | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const openCreate = () => {
    setFormError(null);
    setDraft({ mode: 'create', id: '', start: 0, end: 0 });
  };

  const openEdit = (row: AgeGroupRow) => {
    setFormError(null);
    setDraft({
      mode: 'edit',
      id: row.id,
      start: typeof row.start === 'number' ? row.start : 0,
      end: typeof row.end === 'number' ? row.end : 0,
    });
  };

  const closeForm = () => {
    setDraft(null);
    setFormError(null);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!draft) return;
    setFormError(null);
    const start = draft.start;
    const end = draft.end;
    if (!Number.isInteger(start) || !Number.isInteger(end)) {
      setFormError('Нас бүхэл тоогоор оруулна уу.');
      return;
    }
    if (start < 0 || end < 0 || start > 150 || end > 150) {
      setFormError('Нас 0–150 хооронд байх ёстой.');
      return;
    }
    if (start > end) {
      setFormError('Эхлэх нас дуусах насаас их байж болохгүй.');
      return;
    }
    if (draft.mode === 'edit' && !draft.id.trim()) {
      setFormError('ID алга.');
      return;
    }
    setSaving(true);
    try {
      if (draft.mode === 'create') {
        await createAgeGroup({ start, end });
      } else {
        await updateAgeGroup({ id: draft.id.trim(), start, end });
      }
      closeForm();
      await load({ silent: true });
    } catch (err: unknown) {
      setFormError(
        settingsListApiError(err, draft.mode === 'create' ? 'Нэмэхэд алдаа гарлаа.' : 'Шинэчлэхэд алдаа гарлаа.'),
      );
    } finally {
      setSaving(false);
    }
  };

  const dialogOpen = draft !== null;
  const dialogTitle =
    draft?.mode === 'create' ? 'Шинэ насны ангилал' : draft?.mode === 'edit' ? 'Насны ангилал засах' : '';

  return (
    <>
      <SettingsCrudDialog
        open={dialogOpen}
        title={dialogTitle}
        error={formError}
        saving={saving}
        onClose={closeForm}
        onSubmit={(e) => void handleSubmit(e)}
      >
        {draft && <AgeGroupListForm draft={draft} onChange={setDraft} />}
      </SettingsCrudDialog>

      <SettingsListSection
        title="Насны ангилал"
        subtitle="Насны бүлгийн жагсаалт"
        onAdd={openCreate}
        error={loading ? null : error}
        loading={loading}
        skeletonColumns={4}
      >
        <TableContainer sx={{ maxHeight: { xs: 400, md: 560 } }}>
          <Table size="small" stickyHeader aria-label="Насны ангиллын жагсаалт">
            <TableHead>
              <TableRow>
                <TableCell sx={headSx}>ID</TableCell>
                <TableCell sx={headSx}>Насны ангилал</TableCell>
                <TableCell sx={headSx}>Шинэчилсэн</TableCell>
                <TableCell sx={{ ...headSx, width: 1, whiteSpace: 'nowrap' }} align="right">
                  Үйлдэл
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <SettingsEmptyTableRow colSpan={4} />
              ) : (
                rows.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>{row.id}</TableCell>
                    <TableCell>{ageGroupIntervalLabel(row)}</TableCell>
                    <TableCell>{formatSettingsRowDate(row.updated_at)}</TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0} justifyContent="flex-end">
                        <SettingsListEditButton onEdit={() => openEdit(row)} />
                        <SettingsListDeleteButton
                          id={row.id}
                          deletingId={deletingId}
                          onDelete={deleteById}
                          itemLabel={`ID ${row.id} · ${ageGroupIntervalLabel(row)}`}
                        />
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </SettingsListSection>
    </>
  );
}
